import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatApiError } from "@/lib/api";
import {
  Handshake,
  Users,
  ShieldCheck,
  UserPlus,
  Mail,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";

const MailIcon = () => (
  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
);

const LockIcon = () => (
  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
);

const EyeToggleIcon = ({ open }) =>
  open ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />;

function LoginMark({ height = 56 }) {
  return (
    <img
      src="/brand/logo-mark.png"
      alt="Mabuhay Capital Lending"
      height={height}
      style={{ height, width: height, objectFit: "contain", display: "block" }}
    />
  );
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.full_name || u.email}`);
      nav(`/${u.role}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const quick = (e, p) => {
    setEmail(e);
    setPassword(p);
  };

  return (
    <>
    <AlertDialog open={showForgot} onOpenChange={setShowForgot}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Resetting your password</AlertDialogTitle>
          <AlertDialogDescription>
            Password resets are handled by an administrator - there is no self-service
            reset yet, so no email will be sent. Contact your Mabuhay Capital Lending
            administrator and they can issue you a new temporary password.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => setShowForgot(false)}>Got it</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <div className="min-h-screen bg-[#061b3a]">
      {/* DESKTOP */}
      <div className="hidden lg:grid min-h-screen grid-cols-2 bg-white">
        <div className="relative overflow-hidden text-white bg-[#061b3a]">
          <img
            src="/brand/hero.png"
            alt="Mabuhay Capital Lending"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#061b3a]/95 via-[#061b3a]/60 to-[#061b3a]/15" />

          <div className="relative z-10 p-12">
            <div className="flex items-center gap-4">
              <LoginMark height={62} />
              <div>
                <div className="text-4xl font-bold text-white tracking-wide" style={{ fontFamily: "Outfit" }}>
                  MABUHAY
                </div>
                <div className="text-xs font-semibold tracking-[0.35em] uppercase text-amber-300">
                  Capital Lending
                </div>
              </div>
            </div>

            <div className="mt-48 max-w-xl">
              <h1 className="text-6xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
                Welcome back.
              </h1>
              <p className="mt-5 text-xl text-blue-50/90 leading-snug">
                Your trusted partner for financial support built for Filipinos in Canada.
              </p>
            </div>

            <div className="mt-20 space-y-6">
              <FeatureRow icon={Handshake} title="Built on" sub="Trust" />
              <FeatureRow icon={Users} title="Support for" sub="Your Goals" />
              <FeatureRow icon={ShieldCheck} title="Financial Solutions" sub="You Can Rely On" />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center p-10 bg-white">
          <LoginForm
            submit={submit}
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            loading={loading}
            showPwd={showPwd}
            setShowPwd={setShowPwd}
            quick={quick}
            onForgot={() => setShowForgot(true)}
            desktop
          />
        </div>
      </div>

      {/* MOBILE EXACT FULL-HERO LOOK */}
      <div className="lg:hidden relative min-h-screen overflow-hidden bg-[#061b3a] text-white">
        <img
          src="/brand/hero.png"
          alt="Mabuhay Capital Lending"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: "center top" }}
        />

        <div className="absolute inset-0 bg-gradient-to-b from-[#061b3a]/10 via-transparent to-[#061b3a]/30" />

        <div className="relative z-10 min-h-screen flex flex-col">
          <div className="pt-10 px-6 text-center">
            <div className="flex items-center justify-center gap-3">
              <LoginMark height={58} />
              <div className="text-left">
                <div className="text-3xl font-bold text-white tracking-wide" style={{ fontFamily: "Outfit" }}>
                  MABUHAY
                </div>
                <div className="text-[11px] font-semibold tracking-[0.34em] uppercase text-amber-300">
                  Capital Lending
                </div>
              </div>
            </div>

            <h1 className="mt-10 text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
              Welcome back.
            </h1>
            <p className="mt-3 text-base leading-snug text-white/95">
              Your trusted partner for financial support<br />built for Filipinos in Canada.
            </p>
          </div>

          <div className="mt-auto px-4 pb-5">
            <div className="rounded-[34px] bg-white text-slate-900 shadow-2xl px-7 pt-8 pb-7">
              <LoginForm
                submit={submit}
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                loading={loading}
                showPwd={showPwd}
                setShowPwd={setShowPwd}
                quick={quick}
                onForgot={() => setShowForgot(true)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

function LoginForm({
  submit,
  email,
  setEmail,
  password,
  setPassword,
  loading,
  showPwd,
  setShowPwd,
  quick,
  onForgot,
  desktop = false,
}) {
  return (
    <form onSubmit={submit} className={desktop ? "w-full max-w-md" : "space-y-5"}>
      {desktop && (
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Sign In
          </h1>
          <p className="mt-2 text-slate-500 text-sm">Welcome back! Please sign in to your account.</p>
        </div>
      )}

      <div>
        <Label className="font-bold text-slate-900 text-lg">Email</Label>
        <div className="mt-2 relative">
          <MailIcon />
          <Input
            type="text"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
            className="pl-12 h-14 rounded-xl text-base border-slate-200"
          />
        </div>
      </div>

      <div>
        <Label className="font-bold text-slate-900 text-lg">Password</Label>
        <div className="mt-2 relative">
          <LockIcon />
          <Input
            type={showPwd ? "text" : "password"}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            required
            className="pl-12 pr-12 h-14 rounded-xl text-base border-slate-200"
          />
          <button
            type="button"
            onClick={() => setShowPwd((v) => !v)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <EyeToggleIcon open={showPwd} />
          </button>
        </div>
        <div className="mt-3 text-right">
          <button
            type="button"
            onClick={onForgot}
            className="text-base font-semibold text-blue-700 hover:underline"
          >
            Forgot your password?
          </button>
        </div>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full h-14 text-lg font-bold tracking-wide text-white rounded-xl bg-[#061b3a] hover:bg-[#0a2a5a]"
      >
        {loading ? "SIGNING IN..." : "SIGN IN"}
      </Button>

      <div className="flex items-center gap-4">
        <div className="h-px flex-1 bg-slate-200" />
        <div className="text-base text-slate-600 whitespace-nowrap">Need an account?</div>
        <div className="h-px flex-1 bg-slate-200" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Link
          to="/register"
          className="h-14 rounded-xl border border-blue-600 text-blue-700 font-bold flex items-center justify-center gap-2 hover:bg-blue-50"
        >
          <UserPlus className="h-5 w-5" />
          Sign up
        </Link>
        <button
          type="button"
          onClick={() => quick("admin@loanapp.local", "Admin123!")}
          className="h-14 rounded-xl border border-blue-600 text-blue-700 font-bold flex items-center justify-center gap-2 hover:bg-blue-50"
        >
          <ShieldCheck className="h-5 w-5" />
          Admin
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <button
          type="button"
          onClick={() => quick("admin@loanapp.local", "Admin123!")}
          className="h-10 rounded-lg border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-50"
        >
          Admin
        </button>
        <button
          type="button"
          onClick={() => quick("reviewer@loanapp.local", "Reviewer123!")}
          className="h-10 rounded-lg border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-50"
        >
          Reviewer
        </button>
        <button
          type="button"
          onClick={() => quick("borrower@loanapp.local", "Borrower123!")}
          className="h-10 rounded-lg border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-50"
        >
          Borrower
        </button>
      </div>
    </form>
  );
}

function FeatureRow({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-center gap-5">
      <div className="h-14 w-14 rounded-full border border-amber-300/70 grid place-items-center text-amber-300">
        <Icon className="h-7 w-7" />
      </div>
      <div className="text-lg leading-tight">
        <div>{title}</div>
        <div className="font-bold">{sub}</div>
      </div>
    </div>
  );
}
